import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { fetchSessionInfo, tick, resetSession } from 'store/sessionReducer';

const TICK_INTERVAL = 1000;

const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
    }
};

const sendBrowserNotification = () => {
    if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Gardener 세션 만료 경고', {
            body: '세션이 곧 만료됩니다. 연장하려면 클릭하세요.',
            icon: '/favicon.ico',
            requireInteraction: true,
        }).onclick = () => {
            window.focus();
        };
    }
};

export const useSessionTimer = () => {
    const dispatch = useAppDispatch();
    const userInfo = useAppSelector((state) => state.user.info);
    const showWarning = useAppSelector((state) => state.session.showWarning);
    const remainingSeconds = useAppSelector((state) => state.session.remainingSeconds);
    const tickTimer = useRef<NodeJS.Timeout | null>(null);
    const notified = useRef(false);

    useEffect(() => {
        if (!userInfo) {
            cleanup();
            dispatch(resetSession());
            notified.current = false;
            return;
        }

        // 로그인 시 브라우저 알림 권한 요청
        requestNotificationPermission();

        // 로그인 상태: 세션 정보 가져오기 + 타이머 시작
        dispatch(fetchSessionInfo());
        startTickTimer();

        // 다른 탭에서 세션 연장 시 동기화
        window.addEventListener('storage', handleStorageEvent);

        return () => {
            cleanup();
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, [userInfo]);

    // showWarning이 true로 바뀔 때 브라우저 알림 전송
    useEffect(() => {
        if (showWarning && !notified.current) {
            notified.current = true;
            sendBrowserNotification();
        }
        if (!showWarning) {
            notified.current = false;
        }
    }, [showWarning]);

    // 카운트다운이 0이 되면 서버 확인 → 401이면 로그아웃
    useEffect(() => {
        if (remainingSeconds === 0) {
            dispatch(fetchSessionInfo());
        }
    }, [remainingSeconds]);

    const handleStorageEvent = (e: StorageEvent) => {
        if (e.key === 'GD_SESSION_EXTENDED' || e.key === 'GD_SESSION_WARNING') {
            dispatch(fetchSessionInfo());
        }
    };

    const startTickTimer = () => {
        if (tickTimer.current) clearInterval(tickTimer.current);
        tickTimer.current = setInterval(() => {
            dispatch(tick());
        }, TICK_INTERVAL);
    };

    const cleanup = () => {
        if (tickTimer.current) {
            clearInterval(tickTimer.current);
            tickTimer.current = null;
        }
    };
};
